<template>
    <div class="unique-check">
        <template v-if="isLoading">
            <div class="unique-check__loading-bg" />
            <div class="unique-check__loading">
                <IconLoad class="unique-check__loader" />
                <div class="unique-check__loading-text">
                    Ищем клиента...
                </div>
            </div>
        </template>
        <form v-else
              class="unique-check__form"
              @submit.prevent="triggerGuard">
            <template v-if="isSuccess && userStatus">
                <div :class="['unique-check__status-icon', {'is-not-found': !isUserFound}]">
                    <CheckStatus v-if="isUserFound"
                                 class-name="icon-stroke icon_color_blue" />
                    <CloseSmall v-else
                                class-name="icon-stroke icon_color_gray" />
                </div>
                <p class="unique-check__title">
                    {{ userStatus.title }}
                </p>
                <p class="unique-check__description"
                   v-html="userStatus.description" />
                <div class="unique-check__form-actions">
                    <STBaseButton v-if="!isUserFound"
                                  text="Закрыть"
                                  modify="button_theme_white button_full_width"
                                  @click="hide" />

                    <STBaseButton text="Проверить другого"
                                  :modify="isUserFound ? 'button_theme_white button_full_width' : 'button_full_width'"
                                  @click="resetForm" />

                    <STBaseButton v-if="isUserFound"
                                  text="Записаться в офис"
                                  modify="button_full_width"
                                  @click="createAppointment" />
                </div>
            </template>
            <template v-else-if="isError">
                <div class="unique-check__alert-icon">
                    <IconAlert />
                </div>
                <p class="unique-check__title">
                    Ой, что-то пошло не так
                </p>
                <p class="unique-check__description">
                    Произошла непредвиденная ошибка<br>Пожалуйста попробуйте позже.
                </p>
                <div class="unique-check__form-submit">
                    <STBaseButton text="Хорошо"
                                  modify="button_full_width"
                                  @click="hide" />
                </div>
            </template>
            <template v-else>
                <p class="unique-check__title unique-check__title_theme_top-level">
                    Проверка клиента <br> на уникальность
                </p>

                <div class="unique-check__inputs">
                    <STSelect
                        id="project-select"
                        v-model="selectedProjectId"
                        :options="projects"
                        :multiple="false"
                        name="project-select"
                        label="Выберите проект" />

                    <STInput
                        id="user-phone"
                        ref="userPhoneInput"
                        :value="userPhone"
                        :modify="{'is-error': $v.userPhone.$dirty && $v.userPhone.$invalid}"
                        :error="getErrorText($v.userPhone)"
                        name="user-phone"
                        label="Введите номер телефона клиента"
                        @input="onPhoneInput"
                        @focus="onPhoneFocus" />

                    <STInput
                        id="user-name"
                        v-model.trim="unicClientName"
                        :modify="{'is-error': $v.unicClientName.$dirty && $v.unicClientName.$invalid}"
                        :error="getErrorText($v.unicClientName)"
                        name="request-callback-user-name"
                        label="Введите имя клиента"
                        @input="$v.unicClientName.$touch()"
                        @focus="$v.unicClientName.$touch()" />

                    <STInput
                        id="user-surname"
                        v-model.trim="unicClientSurname"
                        :modify="{'is-error': $v.unicClientSurname.$dirty && $v.unicClientSurname.$invalid}"
                        :error="getErrorText($v.unicClientSurname)"
                        name="request-callback-user-name"
                        label="Введите фамилию клиента"
                        @input="$v.unicClientSurname.$touch()"
                        @focus="$v.unicClientSurname.$touch()" />
                </div>

                <div :class="['unique-check__form-submit', {'is-loading': isLoading}]">
                    <STBaseButton text="Проверить"
                                  modify="button_full_width"
                                  :disabled="!isFieldsFilled"
                                  @click="checkUnique" />
                </div>
            </template>
        </form>
    </div>
</template>

<script>
import CheckStatus from '@/components/Icons/CheckStatus';
import CloseSmall from '@/components/Icons/CloseSmall';
import IconAlert from '@/components/Icons/Alert';
import IconLoad from '@/components/Icons/Load';
import {mapActions, mapGetters} from 'vuex';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';

export default {
    name: 'UniqueCheck',

    components: {
        CheckStatus,
        CloseSmall,
        IconAlert,
        IconLoad
    },

    validations: {
        unicClientName: {
            required,
            letter(value) {
                return /^[A-Za-zА-Яа-яЁё]+$/.test(value);
            },
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        unicClientSurname: {
            required,
            letter(value) {
                return /^[A-Za-zА-Яа-яЁё]+$/.test(value);
            },
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        userPhone: {
            required,
            phoneLength: isPhoneComplete
        }
    },

    data() {
        return {
            isSuccess        : false,
            isError          : false,
            isLoading        : false,
            isUserFound      : null,
            selectedProjectId: '',
            userPhone        : '',
            unicClientName   : '',
            unicClientSurname: ''
        };
    },

    computed: {
        ...mapGetters({
            complexes: 'GET_DISTRICT_LIST'
        }),

        projects() {
            if (!this.complexes?.length) {
                return [];
            }

            return this.complexes.map((complex) => ({
                text : complex.name,
                value: complex.id
            }));
        },

        isFieldsFilled() {
            return this.selectedProjectId && isPhoneComplete(this.userPhone) && !this.$v.$invalid;
        },

        userStatus() {
            if (this.isUserFound === null) {
                return null;
            }

            return this.isUserFound ?
                {
                    title      : 'Клиент уникален',
                    description: `Этого клиента нет в базах данных Tekta&nbsp;Group\nи вы можете продолжить с ним работать<br/><br/>Уведомляем Вас о&nbsp;том, что фиксация клиента производится только <b>при личном визите совместно с&nbsp;клиентом</b> в&nbsp;офис продаж и&nbsp;подписании акта просмотра объекта.`,
                } :
                {
                    title      : 'Клиент не уникален',
                    description: `Этот клиент есть в базах данных ТЕКТА.\nПолучить комиссию по сделке невозможно.`,
                };
        },

        clearPhone() {
            return this.userPhone.replace(/\D+/g, '').replace(/^\d/, '7');
        }
    },

    methods: {
        ...mapActions(['checkUserUnique']),

        getErrorText(value) {
            switch (false) {
                case value.required:
                    return 'Это обязательное поле';
                case value.minLength:
                    return `Минимальная длина ${value.$params?.minLength?.min} символа`;
                case value.maxLength:
                    return `Максимальная длина длина ${value.$params?.maxLength?.max} символов`;
                case value.letter:
                    return 'Допускаются только символы алфавита';
                case value.phoneLength:
                    return 'Это обязательное поле';
            }
        },

        /**
         * Селект при открытии триггерит сабмит формы
         * Данный метод блокирует случайные встроенные сабмиты не по кнопке
         * @return {null}
         */
        triggerGuard() {
            return null;
        },

        /**
         * Отправляет запрос на проверку уникальности юзера
         */
        async checkUnique() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isUserFound = null;
            this.isError = false;
            this.isSuccess = false;
            this.isLoading = true;

            const payload = {
                phone        : this.clearPhone,
                projectId    : this.selectedProjectId,
                clientName   : this.unicClientName,
                clientSurname: this.unicClientSurname
            };

            await this.checkUserUnique(payload)
                .then((response) => {
                    const {data} = response;

                    if (data.error) {
                        const code = data.notCounter ? 1 : 3;

                        this.setErrorState();
                        this.returnError({ code });

                        throw new Error(`В запросе произошла ошибка - ${data.error}`);
                    }

                    // messageCode
                    // 0 - Ошибка интеграции
                    // 1 - Клиент уникальный
                    // 2 - Клиент не уникальный
                    // 3 - Истек срок жизни AccessToken
                    // 4 - Некорректный AccessToken
                    const isShowError = !data.data || !+data.data.messageCode || +data.data.messageCode > 2;
                    const code = 1;

                    if (isShowError) {
                        this.setErrorState();
                        this.returnError({ code });

                        throw new Error(`Ошибка в проверки пользователя на уникальность`);
                    }

                    switch (+data.data.messageCode) {
                        case 1:
                            this.setSuccessState(true);

                            break;
                        case 2:
                            this.setSuccessState(false);

                            break;
                        default:
                            this.setErrorState();
                            this.returnError({ code });

                            throw new Error(`Ошибка при проверке на уникальность`);
                    }
                })
                .catch((error) => {
                    this.setErrorState();
                    this.returnError({
                        code: 5,
                        data: error.response
                    });

                    throw new Error(error);
                });
        },

        createAppointment() {
            const {path} = this.$route;

            if (path !== '/appointments') {
                this.$router.push({
                    name  : 'Appointments',
                    params: {
                        appointmentData: {
                            isCreatingNewAppointment: true,
                            step                    : 2,
                            projectId               : this.selectedProjectId,
                            clientPhone             : this.clearPhone,
                            clientName              : this.unicClientName,
                            clientSurname           : this.unicClientSurname
                        }
                    }
                });
            } else {
                document.dispatchEvent(new CustomEvent('createAppointmentFromSidebar', {
                    detail: {
                        step         : 2,
                        projectId    : this.selectedProjectId,
                        clientPhone  : this.clearPhone,
                        clientName   : this.unicClientName,
                        clientSurname: this.unicClientSurname
                    }
                }));
            }

            this.hide();
        },

        /**
         * Закрывает попап
         */
        hide() {
            this.$v.$reset();
            this.$emit('close');
        },

        /**
         * Сбрасывает форму до изначального состояния
         */
        resetForm() {
            this.isSuccess = false;
            this.isError = false;
            this.isLoading = false;
            this.isUserFound = null;
            this.$v.$reset();
        },

        /**
         * Возвращает ошибку в общую форму
         * @param code {Number} - код ошибки. 1 - ошибка обработки запроса в CRM. 2 - Блокировка на время. 3 - Ошибка со счётчиком попыток. 4 - Блокировка при привышении кол-ва попыток. 5 - Ошибка на сервере
         * @param data {Object} - данные для показа ошибки
         */
        returnError({code, data = null}) {
            this.$emit('showError', {code, data});
        },

        /**
         * Устанавливает флаги для успешного результата проверки на уникальности
         * @param isUserFound {Boolean} - найден ли пользователь
         */
        setSuccessState(isUserFound) {
            this.isSuccess = true;
            this.isUserFound = isUserFound;
            this.isError = false;
            this.isLoading = false;
        },

        /**
         * Устанавливает флаги для ошибки при проверке на уникальность
         */
        setErrorState() {
            this.isError = true;
            this.isLoading = false;
            this.isSuccess = false;
            this.isUserFound = null;
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'userPhone', 'userPhoneInput');
            this.$v.userPhone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'userPhone', 'userPhoneInput', value);
            this.$v.userPhone.$touch();
        },
    }
};
</script>

<style lang="scss">
@import "UniqueCheck";
</style>
